import { useCallback, useState, useEffect } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Button } from '@simplywallst/ui-core';
import LinkOutline from '@simplywallst/ui-core/icons/LinkOutline';
import { useCompanyNoteModal } from '@components/Note';
import { AddNoteSVG } from './styled';

import { useSnackbar } from '@/features/snackbar';

import useUser from '@/hooks/useUser';
import useWindowSize from '@/hooks/useWindowSize';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import If from '@/components/If';
import CompareAndDownload from '@/pages/CompanyReport/components/Overview/components/CompareAndDownload';
import useAddNoteAfterWatchingStock from '@/pages/CompanyReport/hooks/useAddNoteAfterWatchingStock';
import { useFlag } from '@/features/unleash';
import DownloadXlsSolid from '@simplywallst/ui-core/icons/DownloadXlsSolid';
import DownloadPdfSolid from '@simplywallst/ui-core/icons/DownloadPdfSolid';
import TransferSolid from '@simplywallst/ui-core/icons/TransferSolid';
import { push } from 'redux-first-router';
import { ROUTE_COMPARE_COMPANY } from '@/constants/routes';
import { useTranslation } from '@/hooks/useTranslation';
import { getPath } from '@/router/utils';
import { useRegistrationModal } from '@/context/RegistrationModalContext';

export const WATCHLIST_TOGGLE_HEADER = 'watchlist-toggle-header';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal'),
  { ssr: false }
);

const AddRemoveCompany = loadable(
  () => import('@/components/AddRemoveCompany')
);

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle')
);

const WatchlistGuestRegistration = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  {
    resolveComponent: (c) => c.WatchlistGuestRegistration,
  }
);

const NotesButton = ({
  label,
  companyId,
  tickerSymbol,
}: {
  label: string;
  companyId: string;
  tickerSymbol: string;
}) => {
  const isAddNotesAfterWatchingStockEnabled = useAddNoteAfterWatchingStock({
    companyId,
  });

  const { openNoteModal, modalState } = useCompanyNoteModal();

  const { trackEvent } = useTracking();

  if (!isAddNotesAfterWatchingStockEnabled) return null;

  return (
    <Button
      data-cy-id="add-note-button"
      marginLeft
      styleType="louder"
      alignIcon="left"
      disabled={modalState === 'adding'}
      onClick={() => {
        trackEvent({
          action: 'click',
          subject: 'addNote',
        });
        openNoteModal({
          companyId: companyId,
          uniqueSymbol: tickerSymbol,
        });
      }}
    >
      <AddNoteSVG /> {label}
    </Button>
  );
};

interface Props {
  isUserLoggedIn: boolean;
  companyId: string;
  canonicalUrl: string;
  uniqueSymbol: string;
  canGetReportPdf: boolean;
  canGetReportCsv: boolean;
  canGetShareLink: boolean;
  onFetchCompanyCsvReport: (companySymbol: string) => void;
  tickerSymbol: string;
}

type ActiveModal = 'none' | 'addRemoveCompany' | 'registration';

const WatchlistHeaderActions = ({
  companyId,
  isUserLoggedIn,
  canonicalUrl,
  uniqueSymbol,
  canGetReportPdf,
  canGetReportCsv,
  canGetShareLink,
  onFetchCompanyCsvReport,
  tickerSymbol,
}: Props) => {
  const displayCompareButton = useFlag({ name: 'display-compare-button' });
  const { width } = useWindowSize();
  const { isDesktop } = useUserAgent();
  const { shouldShowRego, showRegoModal } = useRegistrationModal();
  const isCompareButtonShown = displayCompareButton.enabled;
  const { trackEvent } = useTracking();
  const { addSnackbar } = useSnackbar();

  const headerButtonsPoint = 390;
  const isSmall = Number(width) <= headerButtonsPoint;
  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const { isSharedCompany, createCompanyShareToken } = useCompanyShareLink();
  const { data: user } = useUser();
  const t = useTranslation();

  const canShowActionButtons = user.loggedIn || !isSharedCompany;
  const isDesktopClient = RUNTIME_ENV !== 'native' && isDesktop;

  useEffect(() => {
    if (isUserLoggedIn && activeModal === 'registration') {
      setActiveModal('addRemoveCompany');
    }
  }, [isUserLoggedIn, activeModal]);

  const createCompanyShareLink = useCallback(() => {
    createCompanyShareToken(
      { companyId },
      {
        onSuccess: (data) => {
          const url = new URL(window.location.href);
          url.searchParams.delete('token');
          url.searchParams.append('token', data);
          navigator.clipboard.writeText(url.href).then(
            () => {
              addSnackbar({
                type: 'positive',
                message: 'Share link copied to clipboard',
                lifeTime: 2000,
              });
            },
            // on error accessing clipboard show link in snackbar
            () => {
              addSnackbar({
                type: 'positive',
                message: 'Share link: ' + url.href,
                actionLabel: 'OK',
              });
            }
          );
          trackEvent({
            type: 'track',
            action: 'click',
            subject: 'Create company share link',
          });
        },
      }
    );
  }, [addSnackbar, companyId, createCompanyShareToken]);

  return (
    <>
      <div id={WATCHLIST_TOGGLE_HEADER} data-cy-id="watchlist-header">
        {canShowActionButtons && (
          <>
            {user.loggedIn ? (
              <WatchlistAddRemoveCompanyToggle
                companyId={companyId}
                tickerSymbol={tickerSymbol}
              />
            ) : (
              <WatchlistGuestRegistration />
            )}

            {user.loggedIn && (
              <NotesButton
                label={t('page.companyReport.header.actions.addNote')}
                companyId={companyId}
                tickerSymbol={tickerSymbol}
              />
            )}

            <Button
              marginLeft
              styleType="solid"
              data-cy-id="add-to-portfolio"
              onClick={() => {
                trackEvent({
                  type: 'track',
                  action: 'click',
                  subject: 'add to portfolio',
                });
                if (isUserLoggedIn) {
                  setActiveModal('addRemoveCompany');
                } else {
                  setActiveModal('registration');
                  trackEvent({
                    type: 'track',
                    action: 'click',
                    modifier: 'addToPortfolio',
                    subject: 'guestRegistrationModal',
                  });
                }
              }}
            >
              {t('page.companyReport.header.actions.addToPortfolio')}
            </Button>
            {isCompareButtonShown && (
              <Button
                className="hidden lg:inline-block"
                marginLeft
                styleType="solid"
                data-cy-id="compare"
                onClick={() => {
                  trackEvent({
                    type: 'track',
                    action: 'click',
                    subject: 'visible-compare',
                  });
                  if (shouldShowRego) {
                    showRegoModal();
                    return;
                  }
                  push(
                    getPath({
                      type: ROUTE_COMPARE_COMPANY,
                      payload: { symbols: encodeURIComponent(uniqueSymbol) },
                    })
                  );
                }}
              >
                {t('page.companyReport.header.actions.compare')}
              </Button>
            )}
          </>
        )}
        {canGetShareLink && (
          <Button
            marginLeft
            styleType="solid"
            onClick={() => createCompanyShareLink()}
          >
            <LinkOutline />
            {isSmall
              ? t('page.companyReport.header.actions.share')
              : t('page.companyReport.header.actions.copyShare')}
          </Button>
        )}
        {isDesktopClient && canShowActionButtons && (
          <CompareAndDownload
            options={[
              ...(!displayCompareButton.enabled
                ? [
                    {
                      label: t('page.companyReport.header.actions.compare'),
                      value: 'compare',
                      icon: <TransferSolid />,
                    },
                  ]
                : []),
              {
                label: t('page.companyReport.header.actions.downloadToPDF'),
                value: 'PDF',
                icon: <DownloadPdfSolid />,
              },
              {
                label: t('page.companyReport.header.actions.downloadToCSV'),
                value: 'CSV',
                icon: <DownloadXlsSolid />,
              },
            ]}
            companyId={companyId}
            tickerSymbol={tickerSymbol}
            canonicalUrl={canonicalUrl}
            uniqueSymbol={uniqueSymbol}
            canGetReportPdf={canGetReportPdf}
            canGetReportCsv={canGetReportCsv}
            isUserLoggedIn={isUserLoggedIn}
            onFetchCompanyCsvReport={onFetchCompanyCsvReport}
          />
        )}
      </div>

      <If
        conditions={{
          showAddRemoveCompany: activeModal === 'addRemoveCompany',
          showRegistration: activeModal === 'registration',
        }}
      >
        {{
          showAddRemoveCompany: () => (
            <AddRemoveCompany
              onClose={() => setActiveModal('none')}
              tickerSymbol={tickerSymbol}
              companyId={companyId}
              uniqueSymbol={uniqueSymbol}
            />
          ),
          showRegistration: () => (
            <GuestRegistrationModal
              canClose
              onClose={() => setActiveModal('none')}
              onRegisterSuccess={() => {
                trackEvent({
                  type: 'track',
                  subject: 'addToPortfolio',
                  modifier: 'registration',
                  action: 'success',
                });
              }}
            />
          ),
        }}
      </If>
    </>
  );
};

export default WatchlistHeaderActions;
